import React, { useEffect, useState } from "react";
import {
  adminListGrants, adminCreateGrant, adminRevokeGrant,
} from "../../lib/api";

/**
 * ReferralsSection — admin issues one-shot referral links worth 30 days
 * of PRO Player or PRO League. Links auto-expire after 90 days if unused.
 */
export function ReferralsSection({ onToast, setAuthed }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [statusFilter, setStatusFilter] = useState("unused");
  const [noteDraft, setNoteDraft] = useState("");

  const reload = async () => {
    try {
      setRows(await adminListGrants(statusFilter));
    } catch (e) {
      if (e?.response?.status === 401) setAuthed(false);
      else onToast?.(e?.response?.data?.detail || "Couldn't load grants.");
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [statusFilter]);

  const issue = async (kind) => {
    setBusy(true);
    try {
      const r = await adminCreateGrant(kind, noteDraft.trim());
      setNoteDraft("");
      const url = `${window.location.origin}${r.redeem_url}`;
      try { await navigator.clipboard.writeText(url); } catch { /* ignore */ }
      onToast?.(`🔗 ${kind === "pro_league" ? "PRO League" : "PRO Player"} link copied to clipboard.`);
      await reload();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't generate link.");
    } finally { setBusy(false); }
  };

  const revoke = async (token) => {
    if (!window.confirm("Revoke this unused referral link?")) return;
    try {
      await adminRevokeGrant(token);
      onToast?.("Link revoked.");
      await reload();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't revoke.");
    }
  };

  const copyUrl = async (token) => {
    const url = `${window.location.origin}/r/grant/${token}`;
    try { await navigator.clipboard.writeText(url); onToast?.("Link copied."); }
    catch { onToast?.("Couldn't copy — select the URL manually."); }
  };

  return (
    <div data-testid="admin-section-referrals-content" className="space-y-5">
      <div className="glass rounded-3xl p-5">
        <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold mb-2">
          <i className="fas fa-gift mr-1" />ISSUE A ONE-SHOT REFERRAL LINK
        </p>
        <p className="text-[11px] text-white/60 mb-3">
          Each link grants 30 days of PRO Player or PRO League — usable once,
          stacks on existing subscriptions, auto-expires after 90 days if
          unredeemed.
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="text"
            value={noteDraft}
            onChange={(e) => setNoteDraft(e.target.value)}
            placeholder="Internal note (optional) — e.g. 'Reddit giveaway'"
            maxLength={200}
            data-testid="admin-grant-note"
            className="flex-1 bg-black/40 border border-white/15 rounded-xl px-3 py-2 text-sm text-white"
          />
          <button
            type="button"
            disabled={busy}
            onClick={() => issue("pro_player")}
            data-testid="admin-issue-pro-player"
            className="px-4 py-2 rounded-xl text-[11px] tracking-widest font-bold btn-neon disabled:opacity-50"
          >
            + PRO PLAYER · 30D
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => issue("pro_league")}
            data-testid="admin-issue-pro-league"
            className="px-4 py-2 rounded-xl text-[11px] tracking-widest font-bold bg-gradient-to-r from-[#14b8a6] to-[#14b8a6] text-black hover:brightness-110 disabled:opacity-50"
          >
            + PRO LEAGUE · 30D
          </button>
        </div>
      </div>

      <div className="flex gap-1 mb-1" data-testid="admin-grants-tabs">
        {[
          { id: "unused", label: "UNUSED" },
          { id: "redeemed", label: "REDEEMED" },
          { id: "all", label: "ALL" },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setStatusFilter(t.id)}
            data-testid={`admin-grants-tab-${t.id}`}
            className={`text-[11px] tracking-widest px-3 py-1.5 rounded-xl ${statusFilter === t.id ? "btn-neon" : "btn-ghost"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {rows.length === 0 ? (
        <p className="text-[11px] text-white/40 py-6 text-center glass rounded-2xl">
          No referral links {statusFilter === "unused" ? "outstanding" : statusFilter === "redeemed" ? "redeemed yet" : "issued yet"}.
        </p>
      ) : (
        <div className="space-y-2" data-testid="admin-grants-list">
          {rows.map((r) => (
            <div key={r.token} data-testid={`admin-grant-row-${r.token}`}
                 className="glass rounded-2xl p-3 flex items-center gap-3">
              <span className={`text-[10px] tracking-[3px] font-bold px-2 py-0.5 rounded-md border ${
                r.kind === "pro_league" ? "bg-[#14b8a6]/15 border-[#14b8a6]/40 text-[#14b8a6]" : "bg-[#14b8a6]/15 border-[#14b8a6]/40 text-[#14b8a6]"
              }`}>{r.kind === "pro_league" ? "PRO LEAGUE" : "PRO PLAYER"}</span>
              <div className="flex-1 min-w-0">
                <code className="text-[11px] text-white/85 truncate block font-mono">
                  /r/grant/{r.token}
                </code>
                <p className="text-[10px] text-white/45 truncate">
                  {r.note || "—"} • created {new Date(r.created_at).toLocaleDateString()}
                  {r.redeemed_by && (<> • redeemed by <span className="text-[#14b8a6]">{r.redeemed_by}</span></>)}
                </p>
              </div>
              {!r.redeemed_by && (
                <>
                  <button
                    type="button" onClick={() => copyUrl(r.token)}
                    data-testid={`admin-grant-copy-${r.token}`}
                    className="px-3 py-1.5 rounded-xl text-[10px] tracking-widest bg-black/40 border border-white/20 hover:bg-white/10"
                  >
                    <i className="fas fa-copy mr-1" />COPY
                  </button>
                  <button
                    type="button" onClick={() => revoke(r.token)}
                    data-testid={`admin-grant-revoke-${r.token}`}
                    className="px-3 py-1.5 rounded-xl text-[10px] tracking-widest bg-[#14b8a6]/15 border border-[#14b8a6]/40 text-[#14b8a6] hover:bg-[#14b8a6]/25"
                  >
                    REVOKE
                  </button>
                </>
              )}
              {r.redeemed_by && (
                <span data-testid={`admin-grant-redeemed-${r.token}`}
                      className="text-[10px] tracking-widest font-bold text-[#14b8a6]">
                  <i className="fas fa-check mr-1" />REDEEMED
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
